import { useState } from 'react';
import type { ReactNode } from 'react';
import { MIN_PASSPHRASE_CHARS } from './passphraseGuard';

/**
 * The unlock phrase, asked for wherever it is needed — first-time setup on
 * the connect route, and again on every cold load of a phrase-guarded
 * browser. One component because the password-manager handling below is
 * subtle enough that two copies would drift.
 */
export function PhraseForm({
  label,
  submit,
  hint,
  error,
  account,
  confirm = false,
  aside,
  busy,
  onSubmit,
}: {
  label: string;
  submit: string;
  hint?: string;
  error?: string | null;
  /** Paired with the phrase so a password manager has a credential to MATCH on a
   *  later visit. Saving worked without it; autofilling did not — a lone
   *  nameless password field is not something a manager can key to this origin.
   *
   *  Suffixed rather than passed bare, so the saved entry cannot collide with the
   *  Google login for the same address on the same origin. Bare, a manager reads
   *  "this site, this email" and offers the ACCOUNT password here — which does
   *  not unlock, and invites overwriting one credential with the other. */
  account?: string;
  confirm?: boolean;
  aside?: ReactNode;
  /** Label for the submit while the phrase is being used; set, the form is locked. */
  busy?: string | null;
  onSubmit: (phrase: string) => void;
}) {
  const [phrase, setPhrase] = useState('');
  const [again, setAgain] = useState('');
  const short = phrase.length > 0 && phrase.length < MIN_PASSPHRASE_CHARS;
  const mismatch = confirm && again.length > 0 && again !== phrase;
  const ready = phrase.length >= MIN_PASSPHRASE_CHARS && (!confirm || again === phrase);

  return (
    <div className="arrive-signin">
      {hint && <p className="arrive-note phrase-hint">{hint}</p>}
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready && !busy) onSubmit(phrase);
        }}
      >
        {/* Off-screen rather than display:none, which managers skip. It gives the
            saved entry a username, which is what makes it findable later. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          className="offscreen"
          value={account ? `${account} (vault)` : 'vault'}
          readOnly
          tabIndex={-1}
          aria-hidden
        />
        <input
          type="password"
          id="vault-unlock-phrase"
          name="vault-unlock-phrase"
          value={phrase}
          onChange={(event) => setPhrase(event.target.value)}
          placeholder={label}
          autoComplete={confirm ? 'new-password' : 'current-password'}
          autoFocus
          required
        />
        {confirm && (
          <input
            type="password"
            id="vault-unlock-phrase-again"
            name="vault-unlock-phrase-again"
            value={again}
            onChange={(event) => setAgain(event.target.value)}
            placeholder="And again"
            autoComplete="new-password"
            required
          />
        )}
        <button type="submit" className="btn-primary" disabled={!ready || !!busy} aria-busy={!!busy}>
          {busy ? (
            <>
              <span className="waiting-dot" /> {busy}
            </>
          ) : (
            submit
          )}
        </button>
      </form>
      {error && <p className="error">{error}</p>}
      {short && <p className="t-metaMono arrive-or">at least {MIN_PASSPHRASE_CHARS} characters</p>}
      {mismatch && <p className="t-metaMono arrive-or">those two do not match</p>}
      {aside}
    </div>
  );
}
